import { useState } from 'react';

import {
  DeleteFilled,
  EditFilled,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';

import { Button, Input, Table, Form } from 'antd';

import { branchListTableHeader as columns } from '@/utils/tableHeader';
import BranchFormModal from './form/BranchModal';
import '@/assets/style/modern-group-list.css';

import { branchApiEndpoints } from '@/api/endpoints';

import { useFetch } from '@/hooks';
import {
  handlePostApi,
  handleUpdatePostApi,
  handleDeleteApi,
} from '@/api/crud';

import { globalSearch } from '@/utils/search/Search';
import GlobalModal from '@/utils/modal/Modal';
import { handleDeleteBranchAPI } from './form/crud';
import { usePostApi } from '@/apiError/apiCreate';
import { useUpdateApi } from '@/apiError/apiUpdate';
import { useDeleteApi } from '@/apiError/apiDelete';
import { useGetApiList } from '@/apiError/apiShow';

const BranchList = () => {
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referenceId, setReferenceId] = useState(null);
  const [calculationType, setCalculationType] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState();

  const { data, isLoading, isSuccess, refresh } = useFetch(
    branchApiEndpoints.retrieveList,
  );

  // const {
  //   data: { data } = {},
  //   isPending: isLoading,
  //   isSuccess,
  //   refetch,
  // } = useGetApiList('/branches/');

  const { mutateAsync: createData, isPending: createLoading } = usePostApi({
    url: '/branches/',
  });
  const { mutateAsync: updateData, isPending: updateLoading } = useUpdateApi();
  const { mutateAsync: deleteData, isPending: deleteLoading } = useDeleteApi();

  const handleCancel = () => {
    setOpenAddModal(false);
    setOpenEditModal(false);
    setOpenDeleteModal(false);
    form.resetFields();
    setReferenceId(null);
    setCalculationType(null);
  };

  const handleOpenAddModal = () => {
    setOpenAddModal(true);
  };

  const handleOpenEditModal = ({ referenceId, ...branch }) => {
    form.resetFields();
    form.setFieldsValue({
      ...branch,
      countryId: branch?.countryID,
      isVatApplicable: branch?.isVatApplicable === 'True' ? true : false,
      isLuxuryTaxApplicable:
        branch?.isLuxuryTaxApplicable === 'True' ? true : false,
    });
    setImageUrl(
      branch?.photo
        ? `${import.meta.env.VITE_APP_API_IMAGE_URL}${branch?.photo}`
        : '',
    );

    setReferenceId(referenceId);
    setCalculationType(branch?.calculationType);
    setOpenEditModal(true);
  };

  const handleOpenDeleteModal = (id) => {
    setReferenceId(id);
    setOpenDeleteModal(true);
  };

  const handleOk = async (data, form) => {
    const formData = new FormData();

    for (const key in data) {
      const value = data[key];

      if (value === undefined) continue;
      if (key === 'photo') {
        let file = value.originFileObj || value;
        const needsRename = file.name.includes('undefined'); // handle completely missing name too
        if (needsRename) {
          file = renameFile(file); // pass form to rename properly now
        }
        if (file instanceof File) {
          formData.append(key, file);
        }
      } else {
        formData.append(key, String(value));
      }
    }

    await createData(formData, {
      'Content-Type': 'multipart/form-data',
    });
    setOpenAddModal(false);
    form.resetFields();
    setCalculationType(null);
    // refetch();
    refresh();
  };

  const renameFile = (compressedFile) => {
    const extension = compressedFile?.name?.split('.').pop();
    const branchName = form.getFieldValue('name')?.split(' ').join('');
    const newFileName = branchName
      ? `${branchName}.${extension}`
      : `undefined.${extension}`;
    const newFile = new File([compressedFile], newFileName, {
      type: compressedFile.type,
    });
    return newFile;
  };

  const handleEditOk = async (data, form) => {
    const formData = new FormData();
    for (const key in data) {
      const value = data[key];

      if (value === undefined || value === null) continue;
      if (key === 'photo') {
        const file = value.originFileObj || value;
        if (file instanceof File) {
          formData.append(key, file);
        } else {
          formData.append(key, String(value));
        }
      } else {
        formData.append(key, String(value));
      }
    }
    await updateData({
      url: `/branch/${referenceId}/`,
      values: formData,
      options: {
        'Content-Type': 'multipart/form-data',
      },
    });
    setOpenEditModal(false);
    form.resetFields();
    // refetch();
    refresh();
  };
  const handleDeleteOk = async () => {
    setOpenDeleteModal(false);
    // handleDeleteBranchAPI(referenceId, refresh, setReferenceId);
    // handleDeleteApi(
    //   referenceId,
    //   branchApiEndpoints.delete,
    //   setLoading,
    //   refresh,
    // );
    await deleteData(`/branch/${referenceId}/`);
    // refetch();
    refresh();
  };

  return (
    <>
      {/*  branch crud modals */}
      {openAddModal && (
        <BranchFormModal
          handleCancel={handleCancel}
          handleOk={handleOk}
          open={openAddModal}
          form={form}
          title="Create Branch"
          calculationType={calculationType}
          renameFile={renameFile}
        />
      )}

      {openEditModal && (
        <BranchFormModal
          handleCancel={handleCancel}
          handleOk={handleEditOk}
          open={openEditModal}
          form={form}
          title="Edit Branch"
          calculationType={calculationType}
          imageUrl={imageUrl}
          setImageUrl={setImageUrl}
          renameFile={renameFile}
          referenceId={referenceId}
        />
      )}
      {/* delete  modal */}

      <GlobalModal
        isVisible={openDeleteModal}
        onConfirm={handleDeleteOk}
        onClose={handleCancel}
        type="delete"
        customContent="Are you sure you want to delete this branch?"
      />

      <div className="modern-group-page">
        <div className="modern-group-header-row">
          <div className="modern-group-header">
            <h1 className="modern-group-title">Branch</h1>
            <p className="modern-group-subtitle">
              Manage and organize your branches
            </p>
          </div>
          <div className="modern-group-actions">
            <Input
              className="header-search"
              name="query"
              placeholder="Search Branch..."
              value={searchQuery}
              prefix={<SearchOutlined />}
              onChange={(event) => setSearchQuery(event.target.value)}
              style={{ width: '300px' }}
            />
            <Button
              className="centered"
              type="primary"
              onClick={handleOpenAddModal}
            >
              <PlusOutlined />
              Create
            </Button>
          </div>
        </div>

        <div className="table-responsive modern-table-wrapper">
          <Table
            size="large"
            bordered
            loading={isLoading || loading}
            columns={columns?.map((col) => ({
              ...col,
              onCell: () => ({
                style: { padding: '6px 12px' },
              }),
            }))}
            className="ant-border-space"
            style={{ textAlign: 'center' }}
            scroll={{ x: true, y: 'calc(100vh - 220px)' }}
            pagination={false}
            dataSource={
              isSuccess
                ? globalSearch(data, searchQuery)?.map((branch, index) => {
                    return {
                      key: branch.referenceId,
                      index: index + 1,
                      company: branch.companyName,
                      code: branch.code,
                      name: branch.name,
                      address: branch.address,
                      calculationType: branch.calculationType,
                      showImage: branch?.showImage,
                      action: (
                        <div className="ant-action tw-flex tw-gap-4 tw-justify-center tw-align-center">
                          <span
                            className="edit tw-inline-flex tw-cursor-pointer hover:tw-bg-blue-200 tw-rounded-full tw-p-1"
                            onClick={() => handleOpenEditModal(branch)}
                          >
                            <EditFilled className="tw-text-blue-600" />
                          </span>
                          <span
                            className="text-danger tw-inline-flex tw-cursor-pointer hover:tw-bg-red-200 tw-rounded-full tw-p-1"
                            onClick={() =>
                              handleOpenDeleteModal(branch.referenceId)
                            }
                          >
                            <DeleteFilled />
                          </span>
                        </div>
                      ),
                    };
                  })
                : []
            }
          />
        </div>
      </div>
    </>
  );
};

export default BranchList;
